/**
 * The one thing that folds on the dispatcher's screens — a trip card on the
 * board and a heavy section on the trip screen are the same component, not
 * two that drift apart.
 *
 * It opens on what a dispatcher needs first and keeps the rest a tap away,
 * so the board reads as a list of trips rather than a wall of detail. What
 * is folded away still SAYS WHAT IT HOLDS while it is closed: a name, and a
 * count wherever there is one ("Passengers · 3"), so nobody has to open a
 * section to find out whether it is worth opening.
 *
 * ACCESSIBILITY, the way the dashboard's own menu groups do it: the handle
 * is a real `<button>` carrying `aria-expanded`, and a closed section is not
 * rendered at all — not hidden with CSS — so a screen reader, the exposure
 * scan's screen text and axe all agree with what is on screen.
 *
 * The handle is named by WHAT IS IN IT — the title and the summary — and
 * never by an `aria-label`. A label would replace the name, and a screen
 * reader would then hear "PU_DASH_… details, collapsed" instead of the time,
 * the route and the step that the eye reads off the same row.
 *
 * Two controls, no fight: whatever the card also offers (Assign, Details)
 * is passed as `actions` and rendered BESIDE the handle, never inside it, so
 * a tap on the row folds and a tap on a control does that control.
 *
 * `remember` is the key a person's own choice is kept under — in the
 * portal's per-device memory (deviceMemory.js), the same store the menu's
 * collapsed groups have always used, so a dispatcher who always wants the
 * map open gets it open next time. It is deliberately absent on a board
 * card: a choice per booking would be a key per booking, kept for ever, for
 * a row that is gone tomorrow.
 */
import React, { useState } from 'react';
import { ChevronRightIcon } from '../Components/Icons';
import { FOLD_STORE, remember, rememberedValue } from '../deviceMemory';

export default function CollapsibleSection({
  title,
  summary = null,
  actions = null,
  // Named `remember` by every caller; renamed inside so the store's own
  // `remember` writer keeps its name.
  remember: rememberKey = '',
  defaultOpen = false,
  className = '',
  children,
}) {
  const [open, setOpen] = useState(() => {
    const chosen = rememberKey ? rememberedValue(FOLD_STORE, rememberKey) : undefined;
    return chosen === undefined ? defaultOpen : Boolean(chosen);
  });

  const toggle = () => setOpen((was) => {
    if (rememberKey) remember(FOLD_STORE, rememberKey, !was);
    return !was;
  });

  return (
    <section className={`pp-fold${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`}>
      <div className="pp-fold__bar">
        <button
          type="button"
          className="pp-fold__handle"
          onClick={toggle}
          aria-expanded={open}
        >
          <ChevronRightIcon size={14} width={2.5} className="pp-fold__chevron" />
          <span className="pp-fold__title">{title}</span>
          {summary ? <span className="pp-fold__summary">{summary}</span> : null}
        </button>
        {actions ? <div className="pp-fold__actions">{actions}</div> : null}
      </div>
      {open ? <div className="pp-fold__body">{children}</div> : null}
    </section>
  );
}
